import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AlertController, IonModal, ToastController } from '@ionic/angular';
import { NaploService } from '../naplo.service';
import { DataService } from '../../data.service';

// NAPLO – OPG (Online Pénztárgép): napi zárások (Z-jelentés) szinkronja gyűjtőkkel + ABCDEF
// ÁFA-kategóriákkal, napi zárásonként PDF + automatikus email egy megadott címre. Itt van a
// KÖZÖS szinkron-időzítő is (osa/opg/önkormányzat/tárhely/nav forrásonként kapcsolható).
@Component({
  selector: 'app-naplo-opg',
  templateUrl: './opg.component.html',
  styleUrls: ['../naplo-shared.scss', './opg.component.scss'],
})
export class OpgComponent implements OnInit {
  @ViewChild('reszletModal') reszletModal: IonModal;
  private readonly base = '/api/naplo-opg';
  get aktiv() { return this.naplo.aktiv; }

  penztargepek: any[] = [];
  napizarasok: any[] = [];
  evSzuro: number | 'mind' = new Date().getFullYear();
  apSzuro: string | 'mind' = 'mind';
  beall: any = { email_cel: '', email_targy: '' };
  reszlet: any = null;          // megnyitott napi zárás (ABCDEF + gyűjtők)
  szinkronFut = false;
  ujPt: any = null;             // új pénztárgép felvétel
  konyvCel: any = { konyveles_mod: 'egyszeres', rovatok: [], penztarak: [], arbevetel: [] };  // könyvelési célpont-választékok
  konyvFut = false;             // napi zárás → könyvelés folyamatban

  constructor(private naplo: NaploService, private http: HttpClient, private toast: ToastController, private alert: AlertController, private data: DataService) {}

  private get<T = any>(p: string) { return firstValueFrom(this.http.get<T>(this.base + p)); }
  private post<T = any>(p: string, b: any) { return firstValueFrom(this.http.post<T>(this.base + p, b)); }
  private put<T = any>(p: string, b: any) { return firstValueFrom(this.http.put<T>(this.base + p, b)); }
  private del<T = any>(p: string) { return firstValueFrom(this.http.delete<T>(this.base + p)); }

  async ngOnInit() {
    await this.naplo.init();
    this.evSzuro = this.aktiv.ev;
    if (this.aktiv.ugyfelId) await this.betolt();
  }

  async betolt() {
    await Promise.all([this.penztargepBetolt(), this.napizarasokBetolt(), this.beallBetolt(), this.celBetolt()]);
  }
  //  Könyvelési célpont-választékok (bevétel rovatok / árbevétel-számlák / pénztárak) + az ügyfél könyvelési módja.
  async celBetolt() {
    try { this.konyvCel = await this.get(`/konyveles-cel/${this.aktiv.ugyfelId}`) || this.konyvCel; }
    catch { this.konyvCel = { konyveles_mod: 'egyszeres', rovatok: [], penztarak: [], arbevetel: [] }; }
  }
  get kettos() { return String(this.konyvCel?.konyveles_mod || '').toLowerCase() === 'kettos'; }
  async penztargepBetolt() { try { this.penztargepek = await this.get(`/penztargepek/${this.aktiv.ugyfelId}`); } catch { this.penztargepek = []; } }
  async napizarasokBetolt() {
    const ev = this.evSzuro !== 'mind' ? '&ev=' + this.evSzuro : '';
    const ap = this.apSzuro !== 'mind' ? '&penztargep=' + encodeURIComponent(this.apSzuro) : '';
    try { this.napizarasok = await this.get(`/napizarasok/${this.aktiv.ugyfelId}?_=1${ev}${ap}`); } catch { this.napizarasok = []; }
  }
  async beallBetolt() { try { this.beall = (await this.get(`/beallitas/${this.aktiv.ugyfelId}`)) || {}; } catch { this.beall = {}; } }

  get evek(): number[] {
    const s = new Set<number>(); s.add(this.aktiv.ev);
    for (const z of this.napizarasok) { const m = String(z.datum || '').match(/(20\d{2})/); if (m) s.add(+m[1]); }
    return [...s].sort((a, b) => b - a);
  }

  // --- pénztárgép-törzs ---
  navPtLista: any[] | null = null;   // a NAV-tól lekért AP-lista (választáshoz)
  navPtFut = false;
  ptUj() { this.ujPt = { ap_szam: '', megnevezes: '' }; this.navPtLista = null; }
  // A NAV-nál nyilvántartott pénztárgépek (AP-számok) automatikus lekérése – ebből választhat a felhasználó.
  async navPtLekard() {
    this.navPtFut = true;
    try {
      const r: any = await this.get(`/nav-penztargepek/${this.aktiv.ugyfelId}`);
      this.navPtLista = r.penztargepek || [];
      if (!this.navPtLista.length) this.ok('A NAV nem adott vissza pénztárgépet ehhez az adószámhoz.');
    } catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült lekérni a NAV-tól.'); }
    finally { this.navPtFut = false; }
  }
  /** A részletezett zárás e-pénztárgépről jött (napi forgalmi jelentés) – más a gyűjtőtábla. */
  get epg(): boolean { return this.reszlet?.forras === 'epg'; }
  /** NAV teszt-környezet: tesztadat generálása a beírt AP-számra, majd teljes szinkron. */
  async tesztadat() {
    const ap = String(this.ujPt?.ap_szam || '').trim().toUpperCase();
    if (!ap) return this.hiba('Előbb írd be az AP-számot.');
    try { await this.post(`/tesztadat/${this.aktiv.ugyfelId}`, { ap_szam: ap }); this.ok('A NAV legenerálta a tesztadatot – most jöhet a szinkron.'); }
    catch (e: any) { this.hiba(e?.error?.error || 'Nem sikerült a tesztadat.'); }
  }
  navPtValaszt(a: any) { if (!this.ujPt) this.ujPt = { ap_szam: '', megnevezes: '' }; this.ujPt.ap_szam = a.ap_szam; }
  async ptMent() {
    if (!this.ujPt?.ap_szam?.trim()) return this.hiba('AP-szám kötelező.');
    try { await this.post(`/penztargep/${this.aktiv.ugyfelId}`, this.ujPt); this.ujPt = null; this.navPtLista = null; await this.penztargepBetolt(); this.ok('Pénztárgép mentve.'); }
    catch { this.hiba('Nem sikerült menteni.'); }
  }
  async ptTorol(p: any) {
    const a = await this.alert.create({ header: 'Törlés', message: `${p.ap_szam} törlése? (a napi zárásokat nem törli)`, buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Törlés', role: 'destructive' }] });
    await a.present(); const { role } = await a.onDidDismiss(); if (role !== 'destructive') return;
    try { await this.del(`/penztargep/${p.id}`); await this.penztargepBetolt(); } catch { this.hiba('Nem sikerült törölni.'); }
  }

  // --- OPG beállítás (email-célcím) ---
  async beallMent() {
    try { await this.put(`/beallitas/${this.aktiv.ugyfelId}`, this.beall); this.ok('Beállítás mentve.'); }
    catch { this.hiba('Nem sikerült menteni.'); }
  }

  // --- kézi szinkron most (NAV OPG letöltés) ---
  async szinkronMost(force = false) {
    if (force && !(await this.megerosit('Teljes újraszinkron', 'Az összes elérhető napi zárás újratöltése a NAV-tól (a meglévők frissülnek az új adatokkal, pl. ÁFA-kulcs). Ez tovább tarthat. Folytatod?'))) return;
    this.szinkronFut = true;
    try {
      const r: any = await this.post(`/szinkron/${this.aktiv.ugyfelId}${force ? '?force=1' : ''}`, {});
      await this.napizarasokBetolt();
      this.ok(force ? `Újraszinkron kész: ${r.uj ?? 0} új, ${r.frissitve ?? 0} frissítve.` : `Szinkron kész: ${r.uj ?? 0} új zárás, ${r.emailezve ?? 0} email kiküldve.`);
    } catch (e: any) { this.hiba(e?.error?.error || 'Szinkron hiba (ellenőrizd a NAV technikai usert az OSA-beállításnál).'); }
    finally { this.szinkronFut = false; }
  }
  // Gyűjtő ÁFA-kategóriája (A–E: a kategória mező, vagy a kód/megnevezés első betűje) és a hozzá tartozó kulcs.
  gyKat(g: any) { return String(g?.kategoria || (g?.kod ? String(g.kod).charAt(0) : (g?.megnevezes || '').charAt(0)) || '—').toUpperCase(); }
  gyAfa(g: any) { const k = this.gyKat(g); const a = (this.reszlet?.afak || []).find((x: any) => x.kategoria === k); return a && a.afa_kulcs != null ? a.afa_kulcs : null; }
  // Van-e olyan gyűjtő, ami csak kategória-szinten (…00) jön? (a register nem bontja szét az azonos kulcsú gyűjtőket)
  get csakKategoria() { const gy = this.reszlet?.gyujtok || []; return gy.length > 0 && gy.every((g: any) => /0{2}$/.test(g.kod || g.megnevezes || '')); }

  // A meglévő zárások hiányzó ÁFA-kulcsainak pótlása a standard leképezésből (A=5/B=18/C=27/D=0/E=0), NAV nélkül.
  async afaPotlas() {
    try { const r: any = await this.post(`/afa-ujraszamit/${this.aktiv.ugyfelId}`, {}); await this.napizarasokBetolt(); this.ok(`ÁFA-kulcs pótolva: ${r.frissitve ?? 0} sor.`); }
    catch { this.hiba('Nem sikerült pótolni az ÁFA-kulcsokat.'); }
  }
  private async megerosit(fejlec: string, uzenet: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.alert.create({ header: fejlec, message: uzenet, buttons: [{ text: 'Mégse', role: 'cancel', handler: () => resolve(false) }, { text: 'Folytatás', handler: () => resolve(true) }] }).then((a) => a.present());
    });
  }

  // --- napi zárás műveletek ---
  async reszletNyit(z: any) {
    try { this.reszlet = await this.get(`/napizaras/${z.id}`); await this.reszletModal.present(); }
    catch { this.hiba('Nem sikerült betölteni a részleteket.'); }
  }
  pdfNyit(z: any) { this.data.megnyitFajl(`${this.base}/pdf/${z.id}`, 'OPG napi zárás PDF'); }
  async emailKuld(z: any) {
    try { const r: any = await this.post(`/email/${z.id}`, {}); r?.kuldve ? this.ok('Email kiküldve: ' + r.cimzett) : this.hiba(r?.ok || 'Nem ment ki email.'); await this.napizarasokBetolt(); }
    catch (e: any) { this.hiba(e?.error?.error || 'Email hiba.'); }
  }

  // --- napi zárás → könyvelés (bevételként, az ügyfél könyvelési módja szerint) ---
  konyveltE(z: any) { return !!(z && (z.konyvelt || z.konyvelt_tetel_id)); }
  async konyvel(z: any) {
    if (this.konyveltE(z)) return this.ok('Ez a zárás már könyvelve van.');
    await this.konyvelIdk([z.id], `Z${z.zaras_szam} felvezetése a könyvelésbe bevételként?`);
  }
  async konyvelNemKonyveltek() {
    const idk = this.napizarasok.filter((z) => !z.konyvelt).map((z) => z.id);
    if (!idk.length) return this.ok('Minden látható zárás könyvelve van.');
    await this.konyvelIdk(idk, `${idk.length} még nem könyvelt napi zárás felvezetése a könyvelésbe bevételként?`);
  }
  private async konyvelIdk(ids: string[], kerdes: string) {
    if (!(await this.megerosit('Felvezetés a könyvelésbe', kerdes + (this.kettos ? ' (kettős: pénztár / árbevétel / fizetendő ÁFA)' : ' (egyszeres: bevétel rovat, ÁFA-kulcsonként)'))) ) return;
    this.konyvFut = true;
    try {
      const r: any = await this.post(`/napizaras/konyvel/${this.aktiv.ugyfelId}`, { ids });
      await this.napizarasokBetolt();
      if (r.konyvelve && this.reszlet && ids.includes(this.reszlet.id)) this.reszlet.konyvelt_tetel_id = true;
      const reszek: string[] = [];
      if (r.konyvelve) reszek.push(`${r.konyvelve} könyvelve${r.cel ? ' (' + r.cel + ')' : ''}`);
      if (r.kihagyva?.length) reszek.push(`${r.kihagyva.length} kihagyva`);
      if (r.hibak?.length) reszek.push(`${r.hibak.length} hiba`);
      if (r.hibak?.length) this.hiba(r.hibak[0]);
      else this.ok(reszek.join(', ') || 'Kész.');
    } catch (e: any) { this.hiba(e?.error?.error || 'A könyvelés nem sikerült.'); }
    finally { this.konyvFut = false; }
  }

  afaOssz(z: any) { return (z?.afak || []).reduce((s: number, a: any) => s + (Number(a.brutto) || 0), 0); }
  private async ok(m: string) { (await this.toast.create({ message: m, duration: 2000, color: 'success' })).present(); }
  private async hiba(m: string) { (await this.toast.create({ message: m, duration: 2800, color: 'danger' })).present(); }
}
